import React from 'react';
import { useSelector } from 'react-redux';
import {
  Card,
  Checkbox,
  Divider,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';
import NumberFormat from 'react-number-format';
import { defaultCustomerGroups } from './helpers';


const RequestPromotionCustomerEligibility = ({
  values,
  handleChange,
  setFieldValue,
  touched,
  errors,
  classes
}) => {
  const { customerGroups = [] } = useSelector((state: IReduxState) => state.customers);
  const allCustomerGroups = [...defaultCustomerGroups, ...customerGroups];

  return (
    <Card className="p-5 mt-4" elevation={0}>
      <Typography variant="h5" className="font-bold mb-5">
        Customer Eligibility <span className="text-error">*</span>
      </Typography>
      <FormControl component="fieldset" className="w-full">
        <RadioGroup
          aria-label="applies to"
          name="customerGroups.name"
          value={values.customerGroups.name}
          onChange={(event) => {
            setFieldValue('customerGroups.name', event.target.value);
          }}
        >
          <FormControlLabel
            value="Everyone"
            control={
              <Radio
                size="small"
                color="primary"
                onClick={() => setFieldValue('customerGroups.value', allCustomerGroups)}
              />}
            label="Everyone"
          />
          <FormControlLabel
            value="Specific group of customers"
            control={
              <Radio
                size="small"
                color="primary"
                onClick={() => setFieldValue('customerGroups.value', [])}
              />}
            label={
              <div className="flex items-center">
                <div className="whitespace-no-wrap">Specific group of customers</div>
                {values.customerGroups.name === 'Specific group of customers' &&
                <Autocomplete
                  multiple
                  fullWidth
                  disableClearable
                  size="small"
                  className="ml-4"
                  value={values.customerGroups.value}
                  options={allCustomerGroups}
                  getOptionLabel={(option) => option.code}
                  onChange={(event, value) => {
                    setFieldValue('customerGroups.value', value);
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      required
                      variant="outlined"
                      placeholder="Choose customer group"
                      error={touched.customerGroups?.value && Boolean(errors.customerGroups?.value)}
                      helperText={touched.customerGroups?.value && errors.customerGroups?.value}
                    />
                  )}
                />
                }
              </div>
            }
          />
          {touched.customerGroups?.name && Boolean(errors.customerGroups?.name) &&
          <FormHelperText>
            <Typography color="error" variant="caption">
              {errors.customerGroups.name}
            </Typography>
          </FormHelperText>
          }
          {values.customerGroups?.name === 'Specific group of customers' &&
          <FormControlLabel
            className="items-start mt-5"
            control={
              <Checkbox
                size="small"
                name="customerGroups.specificGroupSelectionCheckbox"
                color="primary"
                checked={values.customerGroups?.specificGroupSelectionCheckbox}
                onChange={handleChange}
              />
            }
            label={
              <strong className="flex py-2">
                <span>
                  {'I understand that I am selecting a "Specific Group of Customers" and they will need to log in to receive the discount'}
                  <span className="text-error">*</span>
                </span>
              </strong>
            }
          />
          }
          {touched.customerGroups?.specificGroupSelectionCheckbox && Boolean(errors.customerGroups?.specificGroupSelectionCheckbox) &&
          <FormHelperText>
            <Typography color="error" variant="caption">
              {errors.customerGroups?.specificGroupSelectionCheckbox}
            </Typography>
          </FormHelperText>
          }
        </RadioGroup>
      </FormControl>
      <Divider className="my-5" />
      <Typography variant="h5" className="font-bold mb-5">
        Usage Limits
      </Typography>
      <FormControl component="fieldset" className="w-full">
        <FormGroup>
          <FormControlLabel
            control={
              <Checkbox
                color="primary"
                name="usageLimits.perCustomer.checked"
                checked={values.usageLimits.perCustomer.checked}
                onChange={(event) => {
                  const {checked} = event.target;

                  if (!checked) {
                    setFieldValue('usageLimits.perCustomer.value', '');
                  }

                  handleChange(event);
                }}
              />}
            label={
              <div className="flex items-center justify-between">
                <div>Number of times this discount can be used per customer</div>
                <NumberFormat
                  customInput={TextField}
                  size="small"
                  variant="outlined"
                  name="usageLimits.perCustomer.value"
                  className={`max-w-100 ${classes.custom_input}`}
                  value={values.usageLimits.perCustomer.value}
                  onValueChange={(value) => {
                    setFieldValue('usageLimits.perCustomer.value', value.formattedValue);
                    setFieldValue('usageLimits.perCustomer.checked', !!value.formattedValue);
                  }}
                  error={touched.usageLimits?.perCustomer?.value && Boolean(errors.usageLimits?.perCustomer?.value)}
                  helperText={touched.usageLimits?.perCustomer?.value && errors.usageLimits?.perCustomer?.value}
                />
              </div>
            }
          />
          <FormControlLabel
            control={
              <Checkbox
                color="primary"
                name="usageLimits.shoppingQuantity.checked"
                checked={values.usageLimits.shoppingQuantity.checked}
                onChange={(event) => {
                  const {checked} = event.target;

                  if (!checked) {
                    setFieldValue('usageLimits.shoppingQuantity.value', '');
                  }

                  handleChange(event);
                }}
              />}
            label={
              <div className="flex items-center justify-between">
                <div>Maximum quantity in shopping cart the discount can be applied to</div>
                <NumberFormat
                  customInput={TextField}
                  size="small"
                  variant="outlined"
                  name="usageLimits.shoppingQuantity.value"
                  className={`max-w-100 ${classes.custom_input}`}
                  value={values.usageLimits.shoppingQuantity.value}
                  onValueChange={(value) => {
                    setFieldValue('usageLimits.shoppingQuantity.value', value.formattedValue);
                    setFieldValue('usageLimits.shoppingQuantity.checked', !!value.formattedValue);
                  }}
                  error={touched.usageLimits?.shoppingQuantity?.value && Boolean(errors.usageLimits?.shoppingQuantity?.value)}
                  helperText={touched.usageLimits?.shoppingQuantity?.value && errors.usageLimits?.shoppingQuantity?.value}
                />
              </div>
            }
          />
          {values.usageLimits.perCustomer.checked &&
          <FormControlLabel
            className="items-start mt-5"
            control={
              <Checkbox
                size="small"
                name="usageLimits.usageLimitsSelectionCheckbox"
                color="primary"
                checked={values.usageLimits?.usageLimitsSelectionCheckbox}
                onChange={handleChange}
              />
            }
            label={
              <strong className="flex py-2">
                <span>
                  {'I understand that this limit(s) will require the customer to log in to receive the discount'}
                  <span className="text-error">*</span>
                </span>
              </strong>
            }
          />
          }
          {touched.usageLimits?.usageLimitsSelectionCheckbox && Boolean(errors.usageLimits?.usageLimitsSelectionCheckbox) &&
          <FormHelperText>
            <Typography color="error" variant="caption">
              {errors.usageLimits?.usageLimitsSelectionCheckbox}
            </Typography>
          </FormHelperText>
          }
        </FormGroup>
      </FormControl>
    </Card>
  );
};

export default RequestPromotionCustomerEligibility;
